/**
 * Simple API service with guaranteed mock data fallback
 */

import { config } from '@/src/config';
import { debug } from './debug';

export interface ApiResponse<T> {
  data?: T;
  error?: string;
  success: boolean;
}

export interface Referral {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  zip_code: string;
  primary_diagnosis: string;
  urgency_level: string;
  status: string;
  created_at: string;
  needs: string[];
}

export interface CBO {
  id: string;
  name: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  website?: string;
  services: string[];
  zip_codes: string[];
  languages_supported: string[];
  specializations: string[];
  capacity_pct?: number;
  success_rate?: number;
  trust_score?: number;
  rating?: number;
  distance?: number;
}

export interface Metrics {
  total_referrals: number;
  total_feedback: number;
  success_rate: number;
  acceptance_rate: number;
  rejection_rate: number;
  failure_rate: number;
  top_cbos: Array<{
    cbo_id: string;
    name: string;
    success_rate: number;
  }>;
  avg_response_time_days: number;
}

class SimpleApiService {
  private baseURL: string;

  constructor(baseURL: string = config.api.baseUrl) {
    this.baseURL = baseURL;
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    debug.apiRequest(options.method || 'GET', `${this.baseURL}${endpoint}`);

    try {
      // Get auth token and active CBO ID from localStorage
      const token = localStorage.getItem('auth_token');
      const activeCboId = localStorage.getItem('active_cbo_id');

      const url = `${this.baseURL}${endpoint}`;
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...(activeCboId && { 'X-CBO-ID': activeCboId }),
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      debug.apiResponse(response.status, true, data);
      return {
        success: true,
        data,
      };
    } catch (error) {
      debug.warn('API', `Request failed for ${endpoint}, using mock data`, error);
      return this.getMockData<T>(endpoint, options);
    }
  }

  private getMockData<T>(endpoint: string, options: RequestInit = {}): ApiResponse<T> {
    debug.log('API', `Using mock data for ${endpoint}`);
    
    // Parse request body if it exists
    let requestData: any = null;
    if (options.body) {
      try {
        requestData = JSON.parse(options.body as string);
      } catch (e) {
        console.warn('Could not parse request body:', e);
      }
    }
    
    const mockData = {
      '/health': {
        status: "healthy",
        api_version: "1.0.0",
        database: "mock",
        total_users: 2,
        total_cbos: 5,
        total_referrals: 3,
        total_feedback: 1,
      },
      '/api/auth/login': {
        access_token: "mock_jwt_token_12345",
        token_type: "bearer",
        user: {
          id: "user_1",
          name: "Demo User",
          email: requestData?.email || "demo@example.com",
          role: "user",
          created_at: "2025-10-01T00:00:00Z",
          last_login: new Date().toISOString()
        }
      },
      '/api/auth/register': {
        access_token: "mock_jwt_token_12345",
        token_type: "bearer",
        user: {
          id: "user_new",
          name: requestData?.name || "New User",
          email: requestData?.email || "new@example.com",
          role: requestData?.role || "user",
          created_at: new Date().toISOString(),
          last_login: null
        }
      },
      '/api/auth/me': {
        id: "user_1",
        name: "Demo User",
        email: "demo@example.com",
        role: "user",
        created_at: "2025-10-01T00:00:00Z",
        last_login: new Date().toISOString()
      },
      '/api/referrals': [
        {
          id: "ref_1",
          first_name: "John",
          last_name: "Doe",
          email: "john.doe@example.com",
          zip_code: "12345",
          primary_diagnosis: "Diabetes",
          urgency_level: "Routine",
          status: "pending",
          created_at: "2025-10-03T00:00:00Z",
          needs: ["housing", "transportation"]
        },
        {
          id: "ref_2",
          first_name: "Jane",
          last_name: "Smith",
          email: "jane.smith@example.com",
          zip_code: "54321",
          primary_diagnosis: "Hypertension",
          urgency_level: "Urgent",
          status: "matched",
          created_at: "2025-10-02T00:00:00Z",
          needs: ["food_assistance", "medical_transportation"]
        }
      ],
      '/api/cbos': [
        {
          id: "cbo_1",
          name: "Community Health Center",
          contact_email: "info@communityhealth.org",
          contact_phone: "(555) 123-4567",
          address: "123 Main St, City, State 12345",
          website: "https://communityhealth.org",
          services: ["housing", "transportation", "food_assistance"],
          zip_codes: ["12345", "12346", "12347"],
          languages_supported: ["English", "Spanish"],
          specializations: ["homelessness", "mental_health"],
          capacity_pct: 85,
          success_rate: 92,
          trust_score: 4.8,
          rating: 4.9,
          distance: 2.5
        }
      ],
      '/api/metrics': {
        total_referrals: 3,
        total_feedback: 1,
        success_rate: 85.5,
        acceptance_rate: 78.2,
        rejection_rate: 12.3,
        failure_rate: 9.5,
        top_cbos: [
          { cbo_id: "cbo_1", name: "Community Health Center", success_rate: 92 }
        ],
        avg_response_time_days: 2.3
      }
    };

    const data = mockData[endpoint as keyof typeof mockData];
    if (data) {
      return {
        success: true,
        data: data as T,
      };
    }

    return {
      success: false,
      error: `Mock data not available for ${endpoint}`,
    };
  }

  // Health check
  async healthCheck() {
    return this.request<{
      status: string;
      api_version: string;
      database: string;
      total_users: number;
      total_cbos: number;
      total_referrals: number;
      total_feedback: number;
    }>('/health');
  }

  // Referral endpoints
  async getReferrals(): Promise<ApiResponse<Referral[]>> {
    return this.request<Referral[]>('/api/referrals');
  }

  async createReferral(referral: any): Promise<ApiResponse<Referral>> {
    return this.request<Referral>('/api/referrals', {
      method: 'POST',
      body: JSON.stringify(referral),
    });
  }

  // CBO endpoints
  async getCBOs(): Promise<ApiResponse<CBO[]>> {
    return this.request<CBO[]>('/api/cbos');
  }

  // Metrics endpoint
  async getMetrics(): Promise<ApiResponse<Metrics>> {
    return this.request<Metrics>('/api/metrics');
  }

  // Authentication endpoints
  async register(userData: any): Promise<ApiResponse<any>> {
    return this.request<any>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async login(loginData: any): Promise<ApiResponse<any>> {
    return this.request<any>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(loginData),
    });
  }

  async getCurrentUser(token: string): Promise<ApiResponse<any>> {
    return this.request<any>('/api/auth/me');
  }
}

// Export singleton instance
export const apiService = new SimpleApiService();

// Export individual functions for convenience
export const {
  healthCheck,
  getReferrals,
  createReferral,
  getCBOs,
  getMetrics,
  register,
  login,
  getCurrentUser,
} = apiService;
